/**
 * AuditSection 稽核 (S1.4 → S8.1) — record-derived audit facts (created /
 * updated / NetSuite sync) PLUS the PRICE_OVERRIDE review rows from
 * `pricingStore` that belong to THIS product (S8.1, plan §S8.1). Mapped
 * as-is (FD5, zero invented layouts): the existing two-column Event/Time
 * table simply gains rows — an override row renders its store-built summary
 * and reason/actor inside the Event cell, no new table shape.
 *
 * The product filter is the exported pure `selectProductPriceOverrides`
 * (tested in `__tests__/tabComposition.test.tsx`): entity→product resolution
 * joins the store dataset (special_price / currency_price / cost_record rows
 * carry productId; product_pricing IS keyed by productId; so_line entity ids
 * end `:{sku}`). Shared entities (quantity schedules, customer-group
 * discounts, pricing groups, batch headers) are deliberately excluded —
 * editing a shared schedule is not an edit ON this product; those stay in
 * the pricing governance lens.
 *
 * No fabricated rows: the global audit lens (every ReasonRequiredModal
 * write) arrives with governance in W10.
 */

import { useMemo } from 'react';
import { Table, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { useTranslation } from 'react-i18next';
import TableContainer from '@/components/common/TableContainer';
import PurpleTag from '@/components/common/PurpleTag';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { usePricingStore } from '@/stores/pricingStore';
import type { PriceOverrideAuditRow } from '@/stores/pricingStore';
import type { PricingDataset } from '@/types/api/pricing';
import type { Product } from '@/types/api/product';
import SectionHeader from './SectionHeader';

/* eslint-disable react-refresh/only-export-components -- the pure selector
   must live in the SAME module the section renders with (house pattern, see
   CommercialSection routing data) so the composition test exercises the exact
   filter the UI uses, not a re-implementation. */

/**
 * PRICE_OVERRIDE rows for THIS product (pure — dataset+product from caller).
 * Display-only mapping: no price arithmetic here (engine-purity doctrine).
 */
export function selectProductPriceOverrides(
  rows: readonly PriceOverrideAuditRow[],
  dataset: PricingDataset | null,
  product: Pick<Product, 'id' | 'sku'>
): PriceOverrideAuditRow[] {
  return rows.filter((row) => {
    switch (row.entityType) {
      case 'product_pricing':
        // Keyed by productId directly (applyProductPricingSave).
        return row.entityId === product.id;
      case 'special_price':
        return (
          dataset?.specialItemPrices.some(
            (candidate) => candidate.id === row.entityId && candidate.productId === product.id
          ) ?? false
        );
      case 'currency_price':
        return (
          dataset?.currencyPriceRows?.some(
            (candidate) => candidate.id === row.entityId && candidate.productId === product.id
          ) ?? false
        );
      case 'cost_record':
        // S1.2 — cost-basis edits carry the record id; the record has the productId.
        return (
          dataset?.costRecords.some(
            (candidate) => candidate.id === row.entityId && candidate.productId === product.id
          ) ?? false
        );
      case 'so_line':
        // soPriceOverrideAudit entity ids are `${soNumber}:${sku}`.
        return row.entityId.endsWith(`:${product.sku}`);
      case 'quantity_schedule':
      case 'customer_group_discount':
      case 'pricing_group':
      case 'special_price_batch':
        // Shared pricing entities — not edits ON this product (see docblock).
        return false;
    }
  });
}

/* eslint-enable react-refresh/only-export-components */

interface AuditRow {
  key: string;
  timestamp: string;
  /** Record-derived fact rows resolve an i18n eventKey… */
  eventKey?: string;
  /** …PRICE_OVERRIDE rows carry the store row instead (summary/reason/actor). */
  override?: PriceOverrideAuditRow;
}

interface AuditSectionProps {
  product: Product;
}

export const AuditSection = ({ product }: AuditSectionProps) => {
  const { t } = useTranslation(['products', 'common']);
  const { token } = theme.useToken();

  // Read-only store subscription — AuditSection never hydrates or mutates;
  // before any in-session pricing edit both slices are simply empty.
  const auditRows = usePricingStore((state) => state.auditRows);
  const dataset = usePricingStore((state) => state.dataset);

  const overrideRows = useMemo(
    () => selectProductPriceOverrides(auditRows, dataset, product),
    [auditRows, dataset, product]
  );

  const rows: AuditRow[] = [
    { key: 'created', eventKey: 'products:detail.audit.created', timestamp: product.createdAt },
    { key: 'updated', eventKey: 'products:detail.audit.updated', timestamp: product.updatedAt },
    ...(product.netSuiteSyncedAt
      ? [
          {
            key: 'netsuite',
            eventKey: 'products:detail.audit.netSuiteSynced',
            timestamp: product.netSuiteSyncedAt,
          },
        ]
      : []),
    ...overrideRows.map((override) => ({
      key: override.id,
      timestamp: override.createdAt,
      override,
    })),
  ].sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  const columns: TableProps<AuditRow>['columns'] = [
    {
      title: t('products:detail.audit.event'),
      key: 'event',
      render: (_, row) => {
        if (!row.override) return row.eventKey ? t(row.eventKey) : null;
        return (
          <div>
            <div>
              <PurpleTag>{t('products:detail.audit.priceOverride')}</PurpleTag>
              <Typography.Text>{row.override.summary}</Typography.Text>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('products:detail.audit.reasonLine', {
                reason: row.override.reason,
                actor: row.override.actorName,
              })}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: t('products:detail.audit.timestamp'),
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 220,
      render: (timestamp: string) => <TimestampDisplay date={timestamp} showLabel={false} />,
    },
  ];

  return (
    <div>
      <SectionHeader title={t('products:detail.sections.audit')} />
      <TableContainer>
        <Table<AuditRow>
          rowKey="key"
          size="small"
          dataSource={rows}
          columns={columns}
          pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
        />
      </TableContainer>
      <Typography.Text
        type="secondary"
        style={{ display: 'block', marginTop: token.marginSM, fontSize: token.fontSizeSM }}
      >
        {t('products:detail.audit.note')}
      </Typography.Text>
    </div>
  );
};

export default AuditSection;
